import type {IncentiveTier} from 'Types/incentiveTier/incentiveTierTypes';
import {TierType, LoyaltyType} from 'Types/incentiveTier/incentiveTierTypes';
import {DateTime} from 'luxon';
import {centsToString, dollarsToString} from 'Utility/formatters/currency';

export const shouldShowSubtitleList: TierType[] = [
  TierType.TIME,
  TierType.VOLUME,
  TierType.FREE_SHARES,
  TierType.TIME_LOYALTY,
  TierType.VOLUME_LOYALTY,
  TierType.TIME_VOLUME,
  TierType.FREE_SHARES_CODES,
];

export const tierSubtitleIcon: Record<TierType, string> = {
  [TierType.TIME]: 'clock_loader_10',
  [TierType.VOLUME]: 'stacks',
  [TierType.LOYALTY]: 'communities',
  [TierType.FREE_SHARES]: 'communities',
  [TierType.TIME_LOYALTY]: 'clock_loader_10',
  [TierType.VOLUME_LOYALTY]: 'stacks',
  [TierType.TIME_VOLUME]: 'clock_loader_10',
  [TierType.FREE_SHARES_CODES]: 'pin',
};

export const tierBadgeText: Record<TierType, string> = {
  [TierType.TIME]: 'Time',
  [TierType.VOLUME]: 'Volume',
  [TierType.LOYALTY]: 'Loyalty',
  [TierType.FREE_SHARES]: 'Loyalty',
  [TierType.TIME_LOYALTY]: 'Time Loyalty',
  [TierType.VOLUME_LOYALTY]: 'Volume Loyalty',
  [TierType.TIME_VOLUME]: 'Time Volume',
  [TierType.FREE_SHARES_CODES]: 'Consumable codes',
};

export const tierListIcon: Record<TierType, string> = {
  [TierType.TIME]: 'check_circle',
  [TierType.VOLUME]: 'check_circle',
  [TierType.LOYALTY]: 'check_circle',
  [TierType.FREE_SHARES]: 'check_circle',
  [TierType.TIME_LOYALTY]: 'check_circle',
  [TierType.VOLUME_LOYALTY]: 'check_circle',
  [TierType.TIME_VOLUME]: 'check_circle',
  [TierType.FREE_SHARES_CODES]: 'check_circle',
};

const incentiveNotices = {
  [TierType.LOYALTY]: {
    [LoyaltyType.EXISTING]: (companyName: string) =>
      `Existing investors of ${companyName} will automatically receive the perk in the checkout.`,
    [LoyaltyType.REPEAT]: (_: string) =>
      'Investors with multiple investments in this offering will automatically receive the perk in the checkout.',
    [LoyaltyType.RESERVATION]: (companyName: string) =>
      `Investors that reserved shares of ${companyName} will automatically receive the perk in the checkout.`,
  },
};

export const getTierTitle = (
  tier: IncentiveTier,
  issuerPreview = false
): string => {
  if (tier.minimumAmount) {
    switch (tier.tierType) {
      case TierType.VOLUME:
      case TierType.VOLUME_LOYALTY:
      case TierType.TIME_VOLUME:
        return `Invest $${dollarsToString(tier.minimumAmount, {
          removeZeroCents: true,
        })}`;
    }
  }

  switch (tier.tierType) {
    case TierType.TIME:
    case TierType.TIME_LOYALTY:
      return issuerPreview && tier.activeAt ? 'Invest between' : 'Invest by';
    case TierType.TIME_VOLUME:
      return tier.endAt
        ? issuerPreview && tier.activeAt
          ? 'Invest between'
          : 'Invest by'
        : formatTierType(tier.tierType);
    case TierType.LOYALTY:
      return 'Loyalty';
    case TierType.FREE_SHARES:
      return 'Cohort';
    case TierType.FREE_SHARES_CODES:
      return 'Consumable code';
    default:
      return formatTierType(tier.tierType);
  }
};

export const getTierSubtitle = (
  tier: IncentiveTier,
  formatDate = 'd MMM',
  issuerPreview = false
): string => {
  if (tier.minimumAmount) {
    switch (tier.tierType) {
      case TierType.TIME_VOLUME:
        return '+';
    }
  }

  if (tier.endAt) {
    switch (tier.tierType) {
      case TierType.TIME:
      case TierType.TIME_LOYALTY:
      case TierType.TIME_VOLUME:
        return issuerPreview && tier.activeAt
          ? `${formatDateTime(tier.activeAt, formatDate)} - ${formatDateTime(
              tier.endAt,
              formatDate
            )}`
          : `${formatDateTime(tier.endAt, formatDate)}`;
    }
  }

  switch (tier.tierType) {
    case TierType.TIME:
    case TierType.LOYALTY:
    case TierType.TIME_LOYALTY:
    case TierType.TIME_VOLUME:
      return '';
    case TierType.VOLUME:
    case TierType.VOLUME_LOYALTY:
      return '+';
    case TierType.FREE_SHARES:
    case TierType.FREE_SHARES_CODES:
    default:
      return 'offer';
  }
};

export const getTierListMessage = (
  tier: IncentiveTier,
  formatDate = 'd MMM',
  issuerPreview = false
): string => {
  const {tierType, minimumAmount, endAt, activeAt} = tier;

  if (minimumAmount && endAt && tierType === TierType.TIME_VOLUME) {
    const formattedAmountStr = dollarsToString(minimumAmount, {
      removeZeroCents: true,
    });
    const formattedEndDateStr = formatDateTime(endAt, formatDate);
    const formattedStartDateStr = formatDateTime(activeAt, formatDate);
    return issuerPreview
      ? `Invest $${formattedAmountStr} between ${formattedStartDateStr} - ${formattedEndDateStr} and receive:`
      : `Invest $${formattedAmountStr} by ${formattedEndDateStr} and receive:`;
  }

  if (minimumAmount) {
    switch (tierType) {
      case TierType.VOLUME:
      case TierType.VOLUME_LOYALTY:
      case TierType.TIME_LOYALTY:
      case TierType.TIME_VOLUME:
        return `Invest $${dollarsToString(minimumAmount, {
          removeZeroCents: true,
        })} and receive:`;
    }
  }

  if (endAt) {
    switch (tierType) {
      case TierType.TIME:
      case TierType.TIME_VOLUME:
        return issuerPreview && activeAt
          ? `Invest between ${formatDateTime(
              activeAt,
              formatDate
            )} - ${formatDateTime(endAt, formatDate)} and receive:`
          : `Invest by ${formatDateTime(endAt, formatDate)} and receive:`;
    }
  }

  switch (tierType) {
    case TierType.FREE_SHARES:
      return 'Get free shares and receive:';
    case TierType.FREE_SHARES_CODES:
      return 'Use your code and receive:';
    case TierType.LOYALTY:
      return 'Eligible investors will receive:';
    case TierType.TIME:
    case TierType.VOLUME:
    case TierType.TIME_LOYALTY:
    case TierType.VOLUME_LOYALTY:
    case TierType.TIME_VOLUME:
    default:
      return '';
  }
};

export const formatTierType = (tierType: TierType): string => {
  return tierType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
};

export const formatDateTime = (
  dateString: string | undefined,
  format = 'd MMM'
): string => {
  if (!dateString) return '';
  return DateTime.fromISO(dateString).toFormat(format);
};

export const getIncentiveNotice = (
  tier: IncentiveTier,
  companyName: string
): string =>
  incentiveNotices?.[tier.tierType]?.[tier.loyaltyType]?.(companyName) || '';
